import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { initializeAuth } from './initializeAuth';
import { initializeChatView } from './initializeChatView';
import { AppDispatch } from '../../redux/store';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync, logoutAsync } from '../../services/auth/AuthService';
import { loginTeamsAsync } from '../../services/auth/TeamsAuthService';
import { getCachedAuthEnvironment } from '../../services/auth/AuthEnvironmentResolver';
import { alerts } from '../../shared/ui/Alerts';
import { trackException } from '../telemetry/appInsights';

export interface AppStartupState {
    isStartingUp: boolean;
    activeAccount: AccountInfo | null;
    // Teams-only: true when both silent and broker popup token acquisition failed.
    // App.tsx uses this to render Login.tsx so the user can retry acquireTokenPopup.
    teamsAuthFailed: boolean;
}

export interface UseAppStartupArgs {
    msalInstance: IPublicClientApplication;
    dispatch: AppDispatch;
}

export const useAppStartup = ({ msalInstance, dispatch }: UseAppStartupArgs): AppStartupState => {
    const [state, setState] = useState<AppStartupState>({
        isStartingUp: true,
        activeAccount: null,
        teamsAuthFailed: false,
    });

    useEffect(() => {
        let isMounted = true;

        const isTeams = getCachedAuthEnvironment()?.isInTeams ?? false;

        // For Teams, teamsAuthFailed=true causes App.tsx to render Login.tsx for retry.
        // For browser, activeAccount=null causes UnauthenticatedTemplate to render Login.tsx.
        const setAuthFailedState = () => {
            if (!isMounted) return;
            setState({ isStartingUp: false, activeAccount: null, teamsAuthFailed: isTeams });
        };

        const startupAsync = async () => {
            // handle redirect response, ssoSilent, and account cache recovery.
            const auth = await initializeAuth({ msalInstance });
            let resolvedActiveAccount = auth.activeAccount;

            if (!isMounted) return;

            if (!resolvedActiveAccount) {
                trackException(new Error('No active account found during startup.'), {
                    source: 'useAppStartup.noActiveAccount',
                    env: isTeams ? 'Teams' : 'Browser',
                });

                if (isTeams) {
                    // Try the broker popup once. On success, continue startup with the new account.
                    // On failure, show the Teams retry screen (teamsAuthFailed=true).
                    const teamsAccount = await loginTeamsAsync({ msalInstance });
                    if (!teamsAccount) {
                        setAuthFailedState();
                        return;
                    }
                    resolvedActiveAccount = teamsAccount;
                    if (!resolvedActiveAccount) {
                        setAuthFailedState();
                        return;
                    }
                } else {
                    // Browser: clear stale MSAL state so UnauthenticatedTemplate renders Login.tsx.
                    // Teams: setAuthFailedState sets teamsAuthFailed=true which causes App.tsx to render Login.tsx.
                    try {
                        await logoutAsync({ msalInstance });
                        setAuthFailedState();
                    } catch {
                        setAuthFailedState();
                    }
                    return;
                }
            }

            // Acquire a token to verify session validity before loading app data.
            // Browser: acquireBrowserTokenAsync handles silent→redirect→logoutRedirect internally.
            // Teams:   acquireTeamsTokenAsync handles silent→broker popup internally; throws on failure.
            const accessToken = await acquireApiAccessTokenAsync({ msalInstance });

            if (!isMounted) return;

            if (!accessToken) {
                // if no access token, go back to login to reauth
                setAuthFailedState();
                return;
            }

            // Acquire Nexus token for Nexus-specific API calls. Failing here is non-fatal —
            // Nexus provider calls will fail gracefully via Promise.allSettled in the handler.
            const nexusAccessToken = await acquireNexusApiAccessTokenAsync({ msalInstance }).catch(() => '');

            if (!isMounted) return;

            try {
                await initializeChatView({ activeAccount: resolvedActiveAccount, accessToken, nexusAccessToken, dispatch });
            } catch (error) {
                alerts.error('App initialization failed. Please try signing in again.');
                trackException(error, { source: 'useAppStartup.initializeChatView' });
                setAuthFailedState();
                return;
            }

            if (!isMounted) return;

            setState({ isStartingUp: false, activeAccount: resolvedActiveAccount, teamsAuthFailed: false });
        };

        void startupAsync().catch(async (error) => {
            alerts.error('App startup failed. Please try signing in again.');
            console.error('App startup failed.', { error });
            trackException(error, { source: 'useAppStartup.startupAsync' });

            if (isTeams) {
                // can't do logoutredirect in teams, so show login screen to retry
                setAuthFailedState();
            } else {
                try {
                    await logoutAsync({ msalInstance });
                } catch (logoutError) {
                    console.error('Logout after startup failure also failed.', { logoutError });
                    trackException(logoutError, { source: 'useAppStartup.logoutAfterFailure' });
                    setAuthFailedState();
                }
            }
        });

        return () => {
            isMounted = false;
        };
    }, [dispatch, msalInstance]);

    return state;
};
